import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import Enumerable from 'linq';
import * as moment from 'moment-mini';
import { BehaviorSubject, Subject } from 'rxjs';
import { SubSink } from 'subsink';

import { CartService } from '../cart.service';
import { CPP } from '../helpers';
import { MemberTypeModel } from '../model/member-type.model';
import { PackageModel } from '../model/package.model';
import { ProgressBarService } from '../shared/progress-bar/progress-bar.service';
import { TermsComponent } from '../terms/terms.component';
import { WizardStep } from '../wizard-step';
import { WizardService } from '../wizard/wizard.service';
import { MembershipSelectionService } from './membership-selection.service';
import { AppComponent } from '../app.component';
import { TrainingComponent } from '../shared/training/training.component';
import { AppSettings } from 'pac-api';
import { PopupService } from '../shared/popup.service';

enum covidStates {
  none = null,
  working = 1,
  ready = 2
}

@Component({
  selector: 'app-association-level',
  templateUrl: './association-level.component.html',
  styleUrls: ['./association-level.component.css']
})
export class AssociationLevelComponent implements OnInit, OnDestroy, WizardStep {

  static readonly Name = 'AssociationLevelComponent';
  readonly Name = 'AssociationLevelComponent';

  public Title = 'Membership';

  public covidStates = covidStates;
  public COVID$: Subject<covidStates> = new BehaviorSubject(covidStates.none);
  public Busy$: Subject<string> = new BehaviorSubject(null);

  private subsink: SubSink = new SubSink();

  constructor(
    private progress: ProgressBarService,
    private router: Router,
    private route: ActivatedRoute,
    private wizard: WizardService,
    private settings: AppSettings,
    private popup: PopupService,
    private cart: CartService,
    private app: AppComponent,

    // Need this instantiated early even if not directly used
    private service: MembershipSelectionService
  ) {
  }

  public ngOnInit() {

    if (typeof this.wizard.Individual.adr_line1 !== 'string' || this.wizard.Individual.adr_line1 === '') {
      this.router.navigate(['/signup',' address']);
      return;
    }

    this.subsink.sink = this.route.data.subscribe(data => {
      const ecPlus = ('ecPlus' in data && data.ecPlus) || ('ecPlus' in history.state && history.state.ecPlus);
      const freeAssociate = ('freeAssociate' in data && data.freeAssociate) || ('freeAssociate' in history.state && history.state.freeAssociate);

      if (freeAssociate) {
        this.Title = 'Free Month of Associate Membership';
        this.app.wait(true);
        this.COVID$.next(covidStates.working);
        this.Busy$.next('Creating Shopping Cart');
        this.progress.SetActive('selection');

        const associates = {
          'ABMP': '4bf284dd-de53-4d88-8c54-d6756e077d40',
          'ASCP': 'abe6d8dc-1913-4e96-9c87-b6049585aada',
          'AHP': 'd33f0896-6a61-488b-aa84-f65f45ecca5f',
          'ANP': '3353c2ab-f152-4558-93d5-5d80219b7d05'
        };

        this.cart.New().subscribe(cart => {

          this.service.Data.PackageCombo = new PackageModel();

          const today = (new Date()).getTime();

          const memberships = Enumerable.from(cart.ExistingMemberships)
            .where(CPP)
            .where(m => m.mbr_expire_date.getTime() < today)
            .groupBy(m => moment(m.mbr_expire_date).startOf('day').unix())
            .orderByDescending(m => m.key());

          if (memberships.any() === false) {
            this.popup.Open('None of your memberships are eligible for this offer. Please contact us for more help.', { Title: 'Not Eligible' });
            this.app.wait(false);
            return;
          }

          for (const m of memberships.first().toArray()) {
            this.service.Data.Associations[m.mbr_asn_code] = true;
            this.service.Data.Memberships[m.mbr_asn_code] = {
              Membership: associates[m.mbr_asn_code],
              Level: 'Professional',
              Type: new MemberTypeModel({
                mbt_key: associates[m.mbr_asn_code],
                mbt_asn_code: m.mbr_asn_code,
                mbt_code: 'Associate',
                mbt_flip_customer_member_flag: true,
                mbt_includes_insurance_flag_ext: true,
              }),
              Skip: false,
              DiscountCode: null,
            };
          }

          this.Busy$.next('Finding Package Combo');

          this.service.GetPackageCombos().subscribe(results => {
            if (results === null) {
              this.popup.Open('A package combo for your membership levels could not be found. Please contact us for more help.', { Title: 'Package Combos' });
              this.app.wait(false);
              return;
            }

            const p02 = results.find(p02 => p02.p02_combo_price === 0);

            if (p02 === undefined) {
              this.popup.Open('A package combo for your membership levels could not be found. Please contact us for more help.', { Title: 'Package Combos' });
              this.app.wait(false);
              return;
            }

            this.Busy$.next('Adding package to cart');

            this.cart.PackageCombo(p02.p02_key).subscribe(cart => {
              this.wizard.Package = p02;
              this.app.wait(false);
              this.Busy$.next(null);
              this.COVID$.next(covidStates.ready);
            });
          });
        });
      }

      if (ecPlus && this.settings.pac_base_asn_code === 'ABMP') {
        this.Title = 'Exam Coach Plus';

        this.cart.New().subscribe(cart => {
          const mbr = cart.ExistingMemberships
            .filter(m => m.mbr_asn_code === 'ABMP')
            .sort((a, b) =>
              (a.mbr_terminate_date ? a.mbr_terminate_date.getTime() : a.mbr_expire_date.getTime())
              -
              (b.mbr_terminate_date ? b.mbr_terminate_date.getTime() : b.mbr_expire_date.getTime())
            )
            .find(m => m.mbt_flip_customer_member_flag);

          if (mbr) {
            this.service.Data.PackageCombo = new PackageModel();
            this.service.Data.Associations['ABMP'] = true;
            this.service.Data.Memberships['ABMP'] = {
              Membership: mbr.mbt_key,
              Level: 'Student',
              Type: new MemberTypeModel({
                mbt_key: mbr.mbt_code,
                mbt_code: mbr.mbt_key,
                mbt_flip_customer_member_flag: mbr.mbt_flip_customer_member_flag,
                mbt_includes_insurance_flag_ext: mbr.mbt_includes_insurance_flag_ext,
              }),
              Skip: true,
              DiscountCode: null,
            };

            this.router.navigate(['/signup', 'selection', 'options'], { state: { ecPlus: true } });
          } else {
            this.router.navigate(['/signup', 'selection'], { state: {} });
          }
        });
      } else {
        this.progress.AddStep('selection', 'Associations');
        this.progress.AddStep('membership', 'Memberships');
      }

      this.progress.AddStep('options', 'Options');
      this.progress.AddStep('training', 'Training');
      this.progress.HasLastStep = true;
    });

    this.subsink.sink = this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.CleanUpProgress();
      }
    });

    this.CleanUpProgress();
  }

  public ngOnDestroy(): void {
    if (this.subsink) {
      this.subsink.unsubscribe();
    }
  }

  // Jumping into the wizard at the Training or Terms step doesn't properly initialize the progress bar steps
  private CleanUpProgress(): void {
    if (this.route.snapshot.firstChild) {
      if (this.route.snapshot.firstChild.component === TrainingComponent) {
        this.progress.SetActive('training');
      } else if (this.route.snapshot.firstChild.component === TermsComponent) {
        this.progress.Clear();
      }
    }
  }

  public ready(): boolean {
    return true;
  }

  public get HasPromo(): boolean {
    return this.wizard.HasPromo;
  }

  public get Promo(): string {
    return this.wizard.Promo;
  }
}
